/* eslint-disable no-unused-vars */
import { memo, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useComputed, useSignal, useSignalEffect } from '@preact/signals-react';
import { SearchCampaignReports } from '../searchAppPanel/SearchCampaignReports';
import { Loader } from 'components';
import { campaignReportsActions } from 'store/actions';
import { campaignReportsAPI } from 'services/campaignReports.service';
import { getQueryString } from 'utils';
import { TableBody } from './components/TableBody';
import { useFacade } from './useFacade';
import { downloadCSV } from '../exportCSV';
import { selectPb_username } from 'store/selectors';
import * as S from './TableCampaignReports.styled';

const TABLE_ROW_INITIAL_COUNT = 50;
const TABLE_ROW_INCREMENT = 50;

function parseDate(str) {
  if (!str) return;

  const [day, month, year] = str.split('-');
  return new Date(`${year}-${month}-${day}`);
}

function sortBySelectedOrder(array) {
  return array
    .slice()
    .filter(({ selectedOrder }) => selectedOrder !== 0)
    .sort((a, b) => a.selectedOrder - b.selectedOrder)
    .map(({ colName }) => colName);
}

function sortLabelsBySelectedOrder(array) {
  return array
    .slice()
    .filter(({ selectedOrder }) => selectedOrder !== 0)
    .sort((a, b) => a.selectedOrder - b.selectedOrder)
    .map(({ label }) => (label === 'Viewable Impr.' ? 'Viewable Impressions' : label));
}

const getIncludedExcludedStatus = (
  excludedApps$,
  excludedAppsPublisher$,
  pba_number,
  pb_number,
) => {
  if (excludedApps$.value.has(pba_number)) {
    return 'excluded';
  }
  if (excludedAppsPublisher$.value.has(pba_number)) {
    return 'excludedPublisher';
  }
  if (!excludedApps$.value.has(pba_number) || !excludedAppsPublisher$.value.has(pba_number)) {
    return 'included';
  }
};

export const TableCampaignReports = memo(({ dateFrom$, dateTo$ }) => {
  const showAppName = useSelector((state) => state.auth.show_app_name);
  const dispatch = useDispatch();
  const { cmp_id } = useParams();
  const currentRequestType = useSelector((state) => state.campaignReports.tableRequestTypes);
  const sortConfig$ = useSignal(null); // 'ascending', 'descending', or null
  const appFilter = useSelector((state) => state.campaignReports.appFilter);
  const tableAppsList = useSelector((state) => state.campaignReports.tableAppsList);
  const selectedEvents = useSelector((state) => state.campaignReports.selectedEvents);
  const selectedCols = useSelector((state) => state.campaignReports.selectedCols);
  const updatedBidsArray = useSelector((state) => state.campaignReports.updatedBidsArray);
  const appFilter$ = useSignal(appFilter);
  const currentRequestType$ = useSignal(currentRequestType);

  useEffect(() => {
    appFilter$.value = appFilter;
  }, [appFilter]);

  useEffect(() => {
    currentRequestType$.value = currentRequestType;
  }, [currentRequestType]);

  const { selectedAppsOrCountries$, isActive, toggle, toggleAllOff, updateTrigger$ } =
    useFacade(currentRequestType);

  const queryString = getQueryString(
    currentRequestType,
    { from: dateFrom$.value, to: dateTo$.value },
    cmp_id,
  );
  const originalTableData$ = useSignal(null);

  const excludedApps$ = useSignal(new Set());
  const excludedAppsPublisher$ = useSignal(new Set());

  const { data, currentData, isError, isFetching } = campaignReportsAPI.useGetCampaignReportQuery(
    queryString,
    {
      selectFromResult: (response) => {
        if (response?.data?.selectedCols && response?.data?.cmEvents) {
          let selectedCols = JSON.parse(response.data.selectedCols).cols;
          let selectedColLabels = sortLabelsBySelectedOrder(selectedCols);
          selectedColLabels = selectedColLabels.map((el) =>
            el === 'Viewable Impr.' ? 'Viewable Impressions' : el,
          );
          selectedCols = sortBySelectedOrder(selectedCols);
          const selectedEvents = response.data.cmEvents;
          return {
            ...response,
            data: {
              ...response.data,
              selectedTableCols: [...selectedCols],
              selectedColLabels: [...selectedColLabels],
            },
          };
        }
        return response;
      },
      refetchOnMountOrArgChange: true,
    },
  );

  useEffect(() => {
    if (data?.selectedTableCols) {
      dispatch(
        campaignReportsActions.updateProperty({
          propField: 'selectedColLabels',
          newValue: data.selectedColLabels,
        }),
      );
      dispatch(
        campaignReportsActions.updateProperty({
          propField: 'selectedCols',
          newValue: data.selectedTableCols,
        }),
      );
    }
    // check if the redux is empty and there are events
    if (data?.cmEvents?.length) {
      // calculate sums for all events
      let eventsMap = new Map();
      data?.appsData.forEach((item) => {
        // TO-DO fix event_id_arr [null, null, ...] issue !
        // TO-DO fix event_id_arr [null, null, ...] issue !
        // TO-DO fix event_id_arr [null, null, ...] issue !
        item.event_id_arr.forEach((id, ind) => {
          let newValue;
          if (eventsMap.get(id)) {
            newValue = eventsMap.get(id) + item.eventCountArr[ind];
          } else {
            newValue = item.eventCountArr[ind];
          }
          // TO-DO fix event_id_arr [null, null, ...] issue !
          if (id) eventsMap.set(id, newValue);
        });
      });

      // sort sums from max to min
      const orderedArray = Array.from(eventsMap, ([id, value]) => ({ id, value })).sort(
        (a, b) => b.value - a.value,
      );
      // sort server events in the same order as sorted sums
      const eventsArray = structuredClone(data.cmEvents);
      const newReduxValue = orderedArray.map((item, ind) => {
        const cur = eventsArray.find((el) => el.eventId == item.id);
        cur.orderId = ind;
        return cur;
      });

      // store new sorted events in redux and localstorage
      dispatch(
        campaignReportsActions.updateProperty({
          propField: 'selectedEvents',
          newValue: newReduxValue,
        }),
      );
    }

    // clear all changed bids for export
    if (isFetching && currentRequestType === 'Apps') {
      dispatch(campaignReportsActions.clearUpdatedBids());
    }
  }, [isFetching]);

  const { data: allExcludedApps } = campaignReportsAPI.useGetAllExcludedAppsQuery({
    cm_id: cmp_id,
  });

  useEffect(() => {
    if (allExcludedApps) {
      excludedApps$.value = allExcludedApps?.excludedApps;
      excludedAppsPublisher$.value = allExcludedApps?.excludedAppsPublisher;
    }
  }, [allExcludedApps]);

  const filteredData$ = useComputed(() => {
    const requestType = currentRequestType$.value;
    const triggerValue = updateTrigger$.value;
    const filterValue = appFilter$.value;
    const originalData = originalTableData$.value;

    if (originalData && requestType === 'Apps') {
      return originalData.reduce((acc, item) => {
        const newItem = structuredClone(item);

        newItem.appStatus = getIncludedExcludedStatus(
          excludedApps$,
          excludedAppsPublisher$,
          item.pba_number,
          item.pb_number,
        );

        if (
          (selectedAppsOrCountries$.value.size > 0 &&
            selectedAppsOrCountries$.value.has(item.pba_id)) ||
          selectedAppsOrCountries$.value.size === 0
        ) {
          if (filterValue === 'all_status') {
            acc.push(newItem);
          }
          if (filterValue === 'included' && newItem.appStatus === 'included') {
            acc.push(newItem);
          }
          if (filterValue === 'excluded' && newItem.appStatus === 'excluded') {
            acc.push(newItem);
          }
          if (filterValue === 'excluded' && newItem.appStatus === 'excludedPublisher') {
            acc.push(newItem);
          }

          if (
            filterValue === 'custom_bids' &&
            (item.isCustomCpi === 1 || updatedBidsArray.some((obj) => item.pba_number === obj.id))
          ) {
            acc.push(newItem);
          }
        }
        return acc;
      }, []);
    }

    if (originalData && requestType === 'Country') {
      return originalData.reduce((acc, item) => {
        if (
          (selectedAppsOrCountries$.value.size > 0 &&
            selectedAppsOrCountries$.value.has(item.CountryCode)) ||
          selectedAppsOrCountries$.value.size === 0
        ) {
          acc.push(item);
        }
        return acc;
      }, []);
    }

    return originalData;
  });

  useSignalEffect(() => {
    const filteredData = filteredData$.value;
    let newTableAppsListStr = '';
    if (currentRequestType === 'Apps' && Array.isArray(filteredData)) {
      newTableAppsListStr = filteredData?.map(({ pba_id }) => pba_id).join(',');
      newTableAppsListStr !== tableAppsList &&
        dispatch(
          campaignReportsActions.updateProperty({
            propField: 'tableAppsList',
            newValue: newTableAppsListStr,
          }),
        );
    }
  });

  useEffect(() => {
    if (isFetching) return;

    if (sortConfig$.value?.direction && sortConfig$.value?.key) {
      originalTableData$.value = sortItems(
        currentData?.appsData,
        sortConfig$.value?.direction,
        sortConfig$.value?.key,
        updatedBidsArray,
      );
    } else {
      originalTableData$.value = updateBidsIfAny(currentData?.appsData, updatedBidsArray);
    }
  }, [isFetching, queryString]);

  useEffect(() => {
    if (!sortConfig$.value?.direction || !sortConfig$.value?.key) return;

    originalTableData$.value = sortItems(
      originalTableData$.value,
      sortConfig$.value?.direction,
      sortConfig$.value?.key,
      updatedBidsArray,
    );
  }, [sortConfig$.value?.direction, sortConfig$.value?.key]);

  // load more footer
  const numOfRows = filteredData$.value?.length;
  const [visibleRows, setVisibleRows] = useState(
    numOfRows ? Math.min(TABLE_ROW_INITIAL_COUNT, numOfRows) : 0,
  );

  useEffect(() => {
    setVisibleRows(numOfRows ? Math.min(TABLE_ROW_INITIAL_COUNT, numOfRows) : 0);
  }, [numOfRows]);

  const handleLoadMore = () => {
    if (numOfRows === visibleRows) return;
    setVisibleRows((prevVisibleRows) => {
      const newCount = prevVisibleRows + TABLE_ROW_INCREMENT;
      if (newCount > numOfRows) return numOfRows;
      return newCount;
    });
  };

  // export scv
  const username = useSelector(selectPb_username);
  const campaignName = useSelector((state) => state.campaignReports.campaignName);

  const handleExport = useCallback(
    (bids) => {
      const filename = `Campaigns for ${username} - ${campaignName} - by ${currentRequestType} ${formateDateForExportFileName(
        dateFrom$.value,
      )} to ${formateDateForExportFileName(dateTo$.value)}.csv`;
      const dataToExport = filteredData$.value?.map((item) => {
        let currentBid;
        if (bids.some((obj) => obj.id === item.pba_number)) {
          const currentBid = bids.find((obj) => obj.id === item.pba_number);
          return { ...item, cm_app_cpi: currentBid.bid };
        } else {
          return item;
        }
      });
      downloadCSV(dataToExport, selectedEvents, currentRequestType, filename, showAppName);
    },
    [originalTableData$, selectedEvents, currentRequestType, showAppName],
  );

  const isNoData$ = useComputed(() => {
    return !filteredData$.value?.length;
  });

  if (isError) {
    return null;
    // return (
    //   <h1 style={{ padding: '10px 0', border: '1px solid red', color: 'red', textAlign: 'center' }}>
    //     TableCampaignReports Error
    //   </h1>
    // );
  }

  return (
    <S.TableBox>
      <SearchCampaignReports
        isNoData$={isNoData$}
        dateFrom$={dateFrom$}
        dateTo$={dateTo$}
        isActive={isActive}
        toggle={toggle}
        toggleAllOff={toggleAllOff}
        handleExport={handleExport}
        selectedAppsOrCountries$={selectedAppsOrCountries$}
      />
      {isFetching || !originalTableData$.value ? (
        <div style={{ height: `250px`, display: 'flex', alignItems: 'center' }}>
          <Loader.Local delay={0} />
        </div>
      ) : (
        <TableBody
          isNoData$={isNoData$}
          filteredData$={filteredData$}
          sortConfig$={sortConfig$}
          isUa={currentData?.IsUa}
          visibleRows={visibleRows}
          originalTableData$={originalTableData$}
        />
      )}
      {!isFetching && !!numOfRows && (
        <S.TableFooter>
          <S.FooterBtn isActive={visibleRows < numOfRows} onClick={handleLoadMore}>
            Load More
          </S.FooterBtn>
          <S.FooterBody>
            <p>
              Showed: <span>{visibleRows}</span> of <span>{numOfRows}</span>
            </p>
            <S.ProgressBar status={(visibleRows / numOfRows) * 100}></S.ProgressBar>
          </S.FooterBody>
        </S.TableFooter>
      )}
    </S.TableBox>
  );
});

function formateDateForExportFileName(date) {
  if (!date) return '';
  return date.split('/').join('-');
}

function updateBidsIfAny(items, updatedBidsArray) {
  if (!items) return [];
  if (!updatedBidsArray?.length) return [...items];

  return items.map((item) => {
    const r = updatedBidsArray.find((i) => i.id === item.pba_number);
    return { ...item, cm_app_cpi: r ? r.bid : item.cm_app_cpi };
  });
}

const sortItems = (items, direction, key, updatedBidsArray) => {
  // if (updatedBidsArray?.length > 0 && key === 'cm_app_cpi') {
  let sortItems = updateBidsIfAny(items, updatedBidsArray);

  if (!key) return sortItems;

  return sortItems.sort((a, b) => {
    if (key === 'renderedCountries' && a[key] === '-') return 1;

    if (direction === 'ascending') {
      if (key === 'Date') return parseDate(a[key]) - parseDate(b[key]);
      if (typeof a[key] === 'string' && typeof b[key] === 'string') {
        return a[key].localeCompare(b[key]);
      }
      return a[key] < b[key] ? -1 : a[key] > b[key] ? 1 : 0;
    } else if (direction === 'descending') {
      if (key === 'Date') return parseDate(b[key]) - parseDate(a[key]);
      if (typeof a[key] === 'string' && typeof b[key] === 'string') {
        return b[key].localeCompare(a[key]);
      }
      return a[key] > b[key] ? -1 : a[key] < b[key] ? 1 : 0;
    }

    return 0;
  });
};

TableCampaignReports.displayName = 'TableCampaignReports';
